import BasePriceTable from "@/components/base-price/BasePriceTable";
import Breadcrumb from "@/components/Breadcrumb";
import {
  useCreateQuarterExchangeRates,
  useGetCurrencies,
  useGetExchangeRatesByYear,
  useUpdateExchangeRates,
} from "@/hooks/exchangeRateHooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
  NormalSecondaryButton,
} from "@/styles/globalstyles";
import {
  SuccessAlert,
  ConfirmAlert,
  ErrorAlert,
  WarningAlert,
} from "@/utils/alert";
import {
  getCurrentQuarter,
  getUpcomingQuarter,
  getUpcomingQuarterYear,
} from "@/utils/date";
import { Add } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const ExchangeRateManagement = () => {
  const canEdit = usePermissions().canEdit("exchange.rate");
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(dayjs().year());
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFlagged, setIsFlagged] = useState<boolean>(false);
  const [editData, setEditData] = useState<Record<string, string | number>[]>(
    [],
  );

  const {
    data: exchangeRateData,
    isFetching: isFetchingExchangeRates,
    isError: isErrorExchangeRates,
  } = useGetExchangeRatesByYear(year);

  const {
    data: currencies,
    isFetching: isFetchingCurrencies,
    isError: isErrorCurrencies,
  } = useGetCurrencies();

  const { mutate: updateExchangeRate, isPending: isUpdatingExchangeRate } =
    useUpdateExchangeRates();

  const { mutate: createQuarterExchangeRates, isPending: isCreatingQuarter } =
    useCreateQuarterExchangeRates();

  useQueryHandler({
    isFetching:
      isFetchingExchangeRates ||
      isUpdatingExchangeRate ||
      isCreatingQuarter ||
      isFetchingCurrencies,
    isError: isErrorExchangeRates || isErrorCurrencies,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูลอัตราแลกเปลี่ยน",
  });

  const allQuarters: number[] = useMemo(() => {
    const yearData = exchangeRateData?.base_prices;
    return yearData ? yearData.map((item) => item.quarter) : [];
  }, [exchangeRateData]);

  const maxQuarter = allQuarters.length > 0 ? Math.max(...allQuarters) : 0;
  const quarter = selectedQuarter ?? maxQuarter;
  const shouldAddQuarter =
    year === getUpcomingQuarterYear() &&
    (maxQuarter === 0 || maxQuarter < getUpcomingQuarter());

  useEffect(() => {
    setSelectedQuarter(null);
  }, [year]);

  const tableData: Array<Record<string, string | number>> = useMemo(() => {
    if (!quarter) return [];

    return (
      exchangeRateData?.suppliers?.map((supplier) => {
        const rowData: Record<string, string | number> = {
          supplier_name: supplier.name_abbr,
          supplier_id: supplier.id,
        };

        exchangeRateData?.currency?.forEach((currency) => {
          const quarterPrices = exchangeRateData?.base_prices?.find(
            (price) => price.quarter === quarter,
          );
          const priceData = quarterPrices?.data?.find(
            (price) =>
              price.supplier_id === supplier.id &&
              price.currency_id === currency.id,
          );
          rowData[currency.currency_name] = priceData?.base_price || "";
        });
        return rowData;
      }) || []
    );
  }, [quarter, exchangeRateData]);

  const handleChangeQuarterTab = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedQuarter(newValue);
  };

  const handleAddQuarter = () => {
    ConfirmAlert(
      "ยืนยันการเพิ่ม Quarter",
      `คุณต้องการเพิ่ม Exchange Rate สำหรับ Q${getUpcomingQuarter()} ปี ${getUpcomingQuarterYear()} หรือไม่?`,
      "ยืนยัน",
      "ยกเลิก",
    ).then((result) => {
      if (result) {
        proceedAddQuarter();
      }
    });
  };

  const proceedAddQuarter = () => {
    createQuarterExchangeRates(
      {
        target_year: getUpcomingQuarterYear(),
        target_quarter: getUpcomingQuarter(),
      },
      {
        onSuccess: () => {
          SuccessAlert("เพิ่ม Quarter สำเร็จ", "");
          setSelectedQuarter(null);
        },
        onError: () => {
          ErrorAlert("เพิ่ม Quarter ล้มเหลว", "กรุณาลองใหม่อีกครั้ง");
        },
      },
    );
  };

  const handleFlagCheckboxChange = (checked: boolean) => {
    if (checked) {
      WarningAlert(
        "Warning",
        "การบันทึกด้วยตัวเลือกนี้จะทำให้ Part ที่เกี่ยวข้องถูกติด Flagged สำหรับ Buyer ซึ่งต้องให้หัวหน้า Buyer เป็นผู้ปลด Flag เท่านั้น",
      );
    }
    setIsFlagged(checked);
  };

  const handleSave = () => {
    const changedPrices: Array<{
      exchange_rate_price_id: number;
      base_price: number;
    }> = [];

    editData?.forEach((editRow, index) => {
      const originalRow = tableData[index];
      const supplier_id = editRow["supplier_id"];

      exchangeRateData?.currency?.forEach((currency) => {
        const editValue = editRow[currency.currency_name];
        const originalValue = originalRow[currency.currency_name];

        if (editValue !== originalValue) {
          const quarterPrices = exchangeRateData?.base_prices?.find(
            (price) => price.quarter === quarter,
          );
          const priceData = quarterPrices?.data?.find(
            (price) =>
              price.supplier_id === supplier_id &&
              price.currency_id === currency.id,
          );

          if (priceData?.exchange_rate_price_id) {
            changedPrices.push({
              exchange_rate_price_id: priceData.exchange_rate_price_id,
              base_price: parseFloat(editValue as string) || 0,
            });
          }
        }
      });
    });

    if (changedPrices.length === 0) {
      setIsEditing(false);
      setEditData([]);
      setIsFlagged(false);
      return;
    }

    updateExchangeRate(
      { prices: changedPrices, is_buyer_flagged: isFlagged },
      {
        onSuccess: () => {
          SuccessAlert("บันทึกข้อมูลสำเร็จ", "");
          setIsEditing(false);
          setEditData([]);
          setIsFlagged(false);
        },
        onError: () => {
          ErrorAlert("บันทึกข้อมูลล้มเหลว", "กรุณาลองใหม่อีกครั้ง");
        },
      },
    );
  };

  useEffect(() => {
    document.title = "Exchange Rate | AMPA";
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData([...tableData]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData([]);
    setIsFlagged(false);
  };

  const isPastQuarter =
    year < dayjs().year() ||
    (year === dayjs().year() && quarter < getCurrentQuarter());

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="exchange.rate" />
      <Box mt={1}>
        <Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          size="small"
        >
          <MenuItem key={dayjs().year() - 1} value={dayjs().year() - 1}>
            {dayjs().year() - 1}
          </MenuItem>
          <MenuItem key={dayjs().year()} value={dayjs().year()}>
            {dayjs().year()}
          </MenuItem>
          {dayjs().month() === 11 && (
            <MenuItem key={dayjs().year() + 1} value={dayjs().year() + 1}>
              {dayjs().year() + 1}
            </MenuItem>
          )}
        </Select>
        <Box
          mt={1}
          display="flex"
          justifyContent="space-between"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Tabs
              value={quarter}
              onChange={handleChangeQuarterTab}
              aria-label="tabs"
            >
              {allQuarters?.length > 0 &&
                allQuarters.map((q) => (
                  <Tab key={q} label={`Q${q}`} value={q} />
                ))}
            </Tabs>
            {shouldAddQuarter && (
              <Button
                variant="contained"
                size="small"
                onClick={handleAddQuarter}
                disabled={!canEdit}
                sx={{ ...DefaultPrimaryButton, my: 1, fontSize: "0.75rem" }}
              >
                <Add /> เพิ่ม Quarter
              </Button>
            )}
          </Box>
          {!isEditing && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                size="small"
                sx={{ ...DefaultSecondaryButton, fontSize: "0.875rem" }}
                onClick={() => navigate("/exchange-rate/master")}
                disabled={!canEdit}
              >
                จัดการสกุลเงิน
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ ...DefaultSecondaryButton, fontSize: "0.875rem" }}
                onClick={() => navigate("/exchange-rate/supplier")}
                disabled={!canEdit}
              >
                จัดการ Supplier
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleEdit}
                disabled={
                  !canEdit ||
                  quarter <= 0 ||
                  quarter !== maxQuarter ||
                  isPastQuarter
                }
                sx={{ ...DefaultSecondaryButton, fontSize: "0.875rem" }}
              >
                <EditIcon /> แก้ไขอัตราแลกเปลี่ยน
              </Button>
            </Box>
          )}
          {isEditing && (
            <Box sx={{ my: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFlagged}
                    onChange={(e) => handleFlagCheckboxChange(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label="ต้องการให้ติด Flagged สำหรับ Buyer"
                sx={{ mr: 1 }}
              />
              <Button
                variant="outlined"
                sx={{ ...NormalSecondaryButton, fontSize: "0.875rem" }}
                onClick={handleCancel}
              >
                ยกเลิก
              </Button>
              <Button
                variant="outlined"
                sx={{ ...DefaultPrimaryButton, fontSize: "0.875rem" }}
                onClick={handleSave}
              >
                บันทึก
              </Button>
            </Box>
          )}
        </Box>
        <BasePriceTable
          columns={
            currencies
              ?.filter((currency) => currency.is_active)
              ?.map((currency) => currency.currency_name) || []
          }
          data={tableData}
          isEditing={isEditing}
          editData={editData}
          setEditData={setEditData}
        />
      </Box>
    </Box>
  );
};

export default ExchangeRateManagement;
